import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Chip } from '@mui/material';
import * as yup from 'yup';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Appbar from 'components/appbar/Appbar';
import AddressFields from 'components/address/AddressFields';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import InsideLoading from 'components/loading/InsideLoading';
import InsideSaving from 'components/loading/InsideSaving';
import NoData from 'components/no-data/NoData';
import {
  ContentPad,
  PageShell,
  RecordFooter,
  RecordHeader,
  RecordNote,
  StyledForm,
  WorkArea,
} from 'components/record-shell/RecordShell';
import { AddressErrors, emptyAddress, isAddressFilled, validateAddress } from 'helpers/addressValidation';
import { formatCnpj } from 'helpers/documentFormat';
import { api } from 'services/api';
import history from 'services/history';
import { Address } from 'types/address';
import { Bank } from 'types/bank';
import { Hospital, HospitalBank, HospitalProcedure } from 'types/hospital';
import HospitalRecordContent from '../../record/HospitalRecordContent';
import HospitalBanks from '../HospitalBanks';
import HospitalProcedures from '../HospitalProcedures';
import HospitalForm from '../form/HospitalForm';
import HospitalTab from '../tab/HospitalTab';
import { useHospitalValidation } from '../validation/useHospitalValidation';

// todos os campos validados ficam na aba "Dados do hospital"
const FORM_TAB = 'main';

const EditHospital: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [validation, setValidation, validate] = useHospitalValidation();
  // a ficha é a primeira coisa que se vê ao abrir o hospital
  const [tab, setTab] = useState('record');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [clearingAddress, setClearingAddress] = useState(false);
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [savedName, setSavedName] = useState('');
  const [savedActive, setSavedActive] = useState(true);
  const [hospitalAddress, setHospitalAddress] = useState<Address | null>(null);
  const [addressErrors, setAddressErrors] = useState<AddressErrors>({});
  const [banks, setBanks] = useState<Bank[]>([]);
  const [recordReloadKey, setRecordReloadKey] = useState(0);

  const loadHospital = useCallback(() => {
    if (!id) return Promise.resolve();

    return api
      .get(`/hospitals/${id}`)
      .then(response => {
        const hospitalData = response.data;
        setHospital(hospitalData);
        setHospitalAddress(hospitalData.address ?? emptyAddress);
        setSavedName(hospitalData.company_trade_name || hospitalData.company_name);
        setSavedActive(!!hospitalData.active);
      })
      .catch(err => {
        console.error(err);
        toast.error('Não foi possível carregar os dados do hospital');
        history.push('/hospitals');
      });
  }, [id]);

  useEffect(() => {
    setLoading(true);
    loadHospital().finally(() => setLoading(false));
  }, [loadHospital]);

  useEffect(() => {
    fetch('https://brasilapi.com.br/api/banks/v1')
      .then(response => response.json())
      .then(data => setBanks(data.filter((item: Bank) => !!item.code)))
      .catch(error => console.error('Error fetching bank data:', error));
  }, []);

  // a validação guarda o erro do campo; abre a aba onde ele está
  useEffect(() => {
    if (Object.keys(validation).length > 0) setTab(FORM_TAB);
  }, [validation]);

  function goBack() {
    history.push('/hospitals');
  }

  function handleChange<K extends keyof Hospital>(index: K, value: Hospital[K]) {
    setHospital(prev => (prev ? { ...prev, [index]: value } : null));
  }

  function handleAddressChange(address: Address) {
    setHospitalAddress(address);
    setAddressErrors({});
  }

  function handleSubmit() {
    if (!hospital) return;

    setSaving(true);

    api
      .put(`/hospitals/${hospital.id}`, {
        ...hospital,
        address: isAddressFilled(hospitalAddress) ? hospitalAddress : null,
      })
      .then(() => loadHospital())
      .then(() => {
        toast.success('Hospital atualizado');
        setRecordReloadKey(key => key + 1);
        setTab('record');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível atualizar o hospital');
      })
      .finally(() => setSaving(false));
  }

  function handleValidation(e?: FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    if (!hospital) return;

    validate(hospital)
      .then(() => {
        const errors = validateAddress(hospitalAddress);
        setAddressErrors(errors);

        if (Object.keys(errors).length > 0) {
          setTab('address');
          toast.error('Corrija o endereço para salvar');
          return;
        }

        handleSubmit();
        setValidation({});
      })
      .catch((err: yup.ValidationError) => {
        if (err instanceof yup.ValidationError) {
          setValidation({
            [err.path as string]: err.message,
          });
        }
      });
  }

  // no hospital, "excluir" só inativa o cadastro
  function handleInactivateHospital() {
    if (!hospital) return;

    setSaving(true);

    api
      .delete(`/hospitals/${hospital.id}`)
      .then(() => {
        toast.success('Hospital inativado com sucesso!');
        history.push('/hospitals');
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível inativar o hospital');
      })
      .finally(() => {
        setSaving(false);
        setConfirmDialogOpen(false);
      });
  }

  function handleClearAddress() {
    if (!hospital) return;
    setClearingAddress(true);

    api
      .delete(`/hospitals/${hospital.id}/address`)
      .then(() => {
        setHospitalAddress(emptyAddress);
        setAddressErrors({});
      })
      .catch(err => {
        toast.error(err.response?.data?.error || 'Não foi possível limpar o endereço do hospital');
      })
      .finally(() => setClearingAddress(false));
  }

  // procedimentos e contas bancárias são gravados na hora, sem passar pelo Salvar
  function saveList(extra: Partial<Pick<Hospital, 'procedures' | 'banks'>>, failure: string) {
    if (!hospital) return;

    api
      .put(`/hospitals/${hospital.id}`, { ...hospital, ...extra })
      .then(response => {
        toast.success('Salvo');
        setHospital(state =>
          state ? { ...state, procedures: response.data.procedures, banks: response.data.banks } : null,
        );
      })
      .catch(err => {
        toast.error(err.response?.data?.error || failure);
      });
  }

  function handleChangeAddProcedure(procedure: HospitalProcedure) {
    saveList({ procedures: [procedure] }, 'Não foi possível adicionar o procedimento ao hospital');
  }

  function handleDeleteProcedure(procedure: HospitalProcedure) {
    saveList(
      { procedures: [{ ...procedure, active: 0 } as HospitalProcedure] },
      'Não foi possível excluir o procedimento',
    );
  }

  function handleChangeAddBank(bank: HospitalBank) {
    saveList({ banks: [bank] }, 'Não foi possível adicionar a conta bancária');
  }

  function handleDeleteBank(bank: HospitalBank) {
    if (!hospital) return;

    saveList(
      { banks: hospital.banks.map(b => (b.id === bank.id ? ({ ...b, active: 0 } as HospitalBank) : b)) },
      'Não foi possível excluir a conta bancária',
    );
  }

  const isEditing = tab === FORM_TAB || tab === 'address';
  const city = hospitalAddress?.city
    ? `${hospitalAddress.city}${hospitalAddress.state ? ` - ${hospitalAddress.state}` : ''}`
    : '';

  return (
    <>
      <Appbar title="Hospitais" backAction={goBack} />

      {saving && <InsideSaving />}

      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Inativar"
          destructive
          handleNoClick={() => setConfirmDialogOpen(false)}
          handleYesClick={handleInactivateHospital}
          title="Deseja inativar esse hospital?"
        />
      )}

      <PageShell>
        <RecordHeader title={savedName || 'Hospital'} active={savedActive} backAction={goBack}>
          {hospital?.document_number && (
            <Chip size="small" variant="outlined" label={`CNPJ ${formatCnpj(hospital.document_number)}`} />
          )}
          {city && <Chip size="small" variant="outlined" label={city} />}
        </RecordHeader>

        <WorkArea>
          <HospitalTab
            showRecord
            onChange={newTab => setTab(newTab)}
            tab={tab}
            proceduresCount={hospital?.procedures.length}
            banksCount={hospital?.banks.length}
          />
          <ContentPad>
            {loading ? (
              <InsideLoading />
            ) : !hospital ? (
              <NoData message="Não foi possível carregar os dados do hospital" />
            ) : tab === 'record' ? (
              <HospitalRecordContent id={id} reloadKey={recordReloadKey} />
            ) : tab === 'procedures' ? (
              <>
                <HospitalProcedures
                  hospital={hospital}
                  handleDeleteProcedure={handleDeleteProcedure}
                  handleChangeAddProcedure={handleChangeAddProcedure}
                />
                <RecordNote>Adicionar, editar e excluir procedimento salva na hora.</RecordNote>
              </>
            ) : tab === 'banks' ? (
              <>
                <HospitalBanks
                  handleDeleteBank={handleDeleteBank}
                  banks={banks}
                  hospital={hospital}
                  handleChangeAddBank={handleChangeAddBank}
                />
                <RecordNote>Adicionar, editar e excluir conta salva na hora.</RecordNote>
              </>
            ) : (
              <StyledForm onSubmit={handleValidation}>
                {tab === 'address' ? (
                  <AddressFields
                    address={hospitalAddress}
                    onChange={handleAddressChange}
                    errors={addressErrors}
                    onClear={handleClearAddress}
                    clearing={clearingAddress}
                  />
                ) : (
                  <HospitalForm handleChange={handleChange} hospital={hospital} validation={validation} />
                )}
                <button type="submit" style={{ display: 'none' }}></button>
              </StyledForm>
            )}
          </ContentPad>
        </WorkArea>

        {isEditing && !loading && hospital && (
          <RecordFooter sx={{ justifyContent: 'space-between' }}>
            <Button color="error" size="small" disabled={saving} onClick={() => setConfirmDialogOpen(true)}>
              Inativar hospital
            </Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" disabled={saving} onClick={() => handleValidation()}>
                Salvar alterações
              </Button>
            </div>
          </RecordFooter>
        )}
      </PageShell>
    </>
  );
};

export default EditHospital;
